/* ==========================================================================
   Vakantiegeld Subscription Popup — JavaScript
   Campaign: 22 May – 30 June 2026
   ─────────────────────────────────────────────────────────────────────────
   Entry point called by customPopupSubscription() in custom-dev.js:
     window.VakantiegeldPopup.openWithItems(hardwareItems, triggerButton)

   Cart-sync rules:
     Rule 1 – Hardware removed from cart → auto-remove Essentials line
     Rule 2 – VKG Essentials already in cart → skip popup, add hardware only
     Rule 3 – Manual Essentials in cart → skip popup, add hardware only
   (Rules 2 & 3 are checked in customPopupSubscription before calling this)

   Relies on Dawn globals: routes, fetchConfig(), publish(), subscribe(),
   PUB_SUB_EVENTS, cart-drawer / cart-notification elements.
   ========================================================================== */

(function () {
  'use strict';

  /* ── State ──────────────────────────────────────────────────────────────── */
  var state = {
    open:         false,
    cats:         null,
    pendingItems: null,
    lastFocus:    null,
    scrollY:      0,
    initialized:  false
  };
  var dom = {};

  /* ── Public API ─────────────────────────────────────────────────────────── */
  window.VakantiegeldPopup = {
    openWithItems: openWithItems,
    close:         closePopup
  };

  /* ── Init ───────────────────────────────────────────────────────────────── */
  function init() {
    if (state.initialized) return;
    state.initialized = true;

    initCartWatcher(); // Rule 1 — always active on product + cart pages

    dom.popup = document.getElementById('vkg-popup');
    if (!dom.popup) return;

    dom.closeButtons = dom.popup.querySelectorAll('[data-vkg-close]');
    dom.catOptions   = dom.popup.querySelectorAll('[data-vkg-cats]');
    dom.acceptBtn    = dom.popup.querySelector('[data-vkg-accept]');
    dom.declineBtn   = dom.popup.querySelector('[data-vkg-decline]');

    dom.closeButtons.forEach(function (el) {
      el.addEventListener('click', function (e) { e.preventDefault(); closePopup(); });
    });

    dom.catOptions.forEach(function (btn) {
      btn.addEventListener('click', function () {
        selectCats(parseInt(btn.getAttribute('data-vkg-cats'), 10));
      });
    });

    if (dom.acceptBtn)  dom.acceptBtn.addEventListener('click',  acceptOffer);
    if (dom.declineBtn) dom.declineBtn.addEventListener('click', declineOffer);

    document.addEventListener('keyup', function (e) {
      if (state.open && (e.key === 'Escape' || e.key === 'Esc')) closePopup();
    });
  }

  /* ── Entry point ────────────────────────────────────────────────────────── */
  /**
   * Called by customPopupSubscription() after cart-state checks pass.
   * @param {Array}   items  Hardware items [{id, quantity, selling_plan}]
   * @param {Element} btn    The ATC button that was clicked
   * @returns {boolean} true if popup opened, false if it couldn't
   */
  function openWithItems(items, btn) {
    var CONFIG = window.VKG_CONFIG || {};
    if (!CONFIG.mineralEssentialsVariantId) {
      console.warn('[VKG] mineralEssentialsVariantId not set — popup disabled.');
      return false;
    }
    var anyPlan = CONFIG.sellingPlans && (
      CONFIG.sellingPlans[1] || CONFIG.sellingPlans[2] ||
      CONFIG.sellingPlans[3] || CONFIG.sellingPlans[4]
    );
    if (!anyPlan) {
      console.warn('[VKG] No selling plan IDs set yet — accept will no-op until Firmhouse sync.');
    }
    state.pendingItems = items || [];
    state.lastFocus    = btn || document.activeElement;
    openPopup();
    return true;
  }

  /* ── Open / Close ───────────────────────────────────────────────────────── */
  function openPopup() {
    if (state.open || !dom.popup) return;
    selectCats(1);
    setLoading(dom.acceptBtn,  false);
    setLoading(dom.declineBtn, false);

    state.scrollY = window.scrollY;
    dom.popup.hidden = false;
    document.body.classList.add('vkg-popup-open');
    document.body.style.top = '-' + state.scrollY + 'px';
    state.open = true;

    requestAnimationFrame(function () {
      var first = dom.popup.querySelector('[data-vkg-cats]');
      if (first) first.focus();
    });
  }

  function closePopup() {
    if (!state.open || !dom.popup) return;
    dom.popup.hidden = true;
    document.body.classList.remove('vkg-popup-open');
    document.body.style.top = '';
    window.scrollTo(0, state.scrollY || 0);
    state.open         = false;
    state.pendingItems = null;
    if (state.lastFocus && typeof state.lastFocus.focus === 'function') {
      state.lastFocus.focus();
    }
  }

  /* ── Cat selection ──────────────────────────────────────────────────────── */
  function selectCats(cats) {
    state.cats = cats ? parseInt(cats, 10) : null;
    if (!dom.catOptions) return;
    dom.catOptions.forEach(function (btn) {
      var active = parseInt(btn.getAttribute('data-vkg-cats'), 10) === state.cats;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-checked', active ? 'true' : 'false');
    });
    if (dom.acceptBtn) dom.acceptBtn.disabled = (state.cats === null);
  }

  /* ── Accept: hardware + Essentials together ─────────────────────────────── */
  function acceptOffer() {
    if (!dom.acceptBtn || state.cats === null) return;

    var CONFIG = window.VKG_CONFIG || {};
    var planId = CONFIG.sellingPlans && CONFIG.sellingPlans[state.cats];

    if (!planId) {
      console.warn('[VKG] Selling plan ID missing for ' + state.cats + ' cat(s). Fill sub-popup-plan-id-' + state.cats + ' in the customizer after Firmhouse sync.');
      closePopup();
      return;
    }

    setLoading(dom.acceptBtn, true);

    var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    var hwItems = (state.pendingItems || []).map(function (p) {
      return { id: p.id, quantity: p.quantity || 1, properties: p.properties, selling_plan: p.selling_plan };
    });

    var essItem = {
      id:           parseInt(CONFIG.mineralEssentialsVariantId, 10),
      quantity:     1,
      selling_plan: parseInt(planId, 10),
      properties: {
        '_vkg_source': 'vakantiegeld',
        '_vkg_hw':     hwItems.length ? String(hwItems[0].id) : ''
      }
    };

    var formData = {
      items:    hwItems.concat([essItem]),
      sections: cart && cart.getSectionsToRender ? cart.getSectionsToRender().map(function (s) { return s.id; }) : []
    };

    var cfg = fetchConfig('json');
    cfg.headers['X-Requested-With'] = 'XMLHttpRequest';
    cfg.body = JSON.stringify(formData);

    fetch(routes.cart_add_url, cfg)
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (res.errors) throw new Error(res.errors);
        closePopup();
        if (cart) {
          cart.renderContents(Object.assign({}, res.items[0], { sections: res.sections }));
          cart.classList.remove('is-empty');
        }
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-form',
          productVariantId: hwItems[0] ? hwItems[0].id : parseInt(CONFIG.mineralEssentialsVariantId, 10)
        });
      })
      .catch(function (err) {
        console.error('[VKG] Accept failed:', err);
        setLoading(dom.acceptBtn, false);
        alert('Er ging iets mis bij het toevoegen aan je winkelmandje. Probeer het nogmaals.');
      });
  }

  /* ── Decline: hardware only ─────────────────────────────────────────────── */
  function declineOffer() {
    setLoading(dom.declineBtn, true);

    var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    var hwItems = (state.pendingItems || []).map(function (p) {
      return { id: p.id, quantity: p.quantity || 1, properties: p.properties, selling_plan: p.selling_plan };
    });

    var formData = {
      items:    hwItems,
      sections: cart && cart.getSectionsToRender ? cart.getSectionsToRender().map(function (s) { return s.id; }) : []
    };

    var cfg = fetchConfig('json');
    cfg.headers['X-Requested-With'] = 'XMLHttpRequest';
    cfg.body = JSON.stringify(formData);

    fetch(routes.cart_add_url, cfg)
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (res.errors) throw new Error(res.errors);
        closePopup();
        if (cart) {
          cart.renderContents(Object.assign({}, res.items[0], { sections: res.sections }));
          cart.classList.remove('is-empty');
        }
        if (hwItems.length) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: hwItems[0].id });
        }
      })
      .catch(function (err) {
        console.error('[VKG] Decline failed:', err);
        setLoading(dom.declineBtn, false);
        alert('Er ging iets mis bij het toevoegen aan je winkelmandje. Probeer het nogmaals.');
      });
  }

  /* ── Rule 1: cart watcher ───────────────────────────────────────────────── */
  function initCartWatcher() {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      syncEssentials();
    });
  }

  function syncEssentials() {
    fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var vkgLine = null;
        for (var i = 0; i < cart.items.length; i++) {
          if (cart.items[i].properties && cart.items[i].properties['_vkg_source'] === 'vakantiegeld') {
            vkgLine = cart.items[i]; break;
          }
        }
        if (!vkgLine) return;

        var hwId = vkgLine.properties['_vkg_hw'];
        if (!hwId) return;

        var hwPresent = cart.items.some(function (item) {
          return String(item.variant_id) === String(hwId);
        });
        if (hwPresent) return;

        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: vkgLine.key, quantity: 0 })
        })
          .then(function (r) { return r.json(); })
          .then(function () { refreshCartUI(); })
          .catch(function (e) { console.warn('[VKG] Failed to remove Essentials:', e); });
      })
      .catch(function (e) { console.warn('[VKG] Cart sync failed:', e); });
  }

  function refreshCartUI() {
    var cartEl = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    if (!cartEl || typeof cartEl.getSectionsToRender !== 'function') return;
    var ids = cartEl.getSectionsToRender().map(function (s) { return s.id; });
    if (!ids.length) return;
    fetch(window.Shopify.routes.root + '?sections=' + ids.join(','))
      .then(function (r) { return r.json(); })
      .then(function (sections) { cartEl.renderContents({ sections: sections }); })
      .catch(function (e) { console.warn('[VKG] Cart UI refresh failed:', e); });
  }

  /* ── Helpers ────────────────────────────────────────────────────────────── */
  function setLoading(btn, on) {
    if (!btn) return;
    var txt = btn.querySelector('.vkg-cta__text');
    var spn = btn.querySelector('.vkg-cta__spinner');
    if (on) {
      btn.setAttribute('disabled', 'disabled');
      if (txt) txt.style.opacity = '.5';
      if (spn) spn.hidden = false;
    } else {
      btn.removeAttribute('disabled');
      if (btn === dom.acceptBtn && state.cats === null) btn.disabled = true;
      if (txt) txt.style.opacity = '';
      if (spn) spn.hidden = true;
    }
  }

  /* ── Boot ───────────────────────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();